import { UserProfile } from 'fitbeat.gym-core';
import { ISubscriptionPaymentSummary } from 'fitbeat.models';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
import { PaywallPage } from '../auth/types';
import { Button, ButtonSize, ButtonTypes } from '../components';
import { IAppStore } from '../HomeWorkouts';
import { PaymentSummaryDisplay } from '../payments/components/PaymentSummaryDisplay';
import TrialSubscriptionSummary from '../payments/components/TrialSubscriptionSummary';
import { openPaywallModalAction } from '../payments/redux/payments-actions';
import Theme from '../theme';
import { AppStateFocusedEvents, showMessage } from '../ui';
import { UserDataConsumer } from '../utils';
import { Logger } from '../utils';
import { createUserServiceClient } from '../utils/apiClientFactory';
import { defaultScale } from '../utils/scaling';
import { openLink } from '../utils/linkUtils';

interface IProps {
  openPaywallModal: (onJoinSuccess: () => void) => void;
  user: {
    userProfile: UserProfile;
  };
  navigation: any;
  t: (key: string) => string;
}

interface IState {
  isLoading: boolean;
  paymentSummary: ISubscriptionPaymentSummary[];
  activatePendingLoading: boolean;
  customerPortalLoading: boolean;
  paymentInfo: any;
}

class BillingTab extends Component<IProps, IState> {
  private readonly userServiceClient = createUserServiceClient();

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      paymentSummary: [],
      activatePendingLoading: false,
      customerPortalLoading: false,
      paymentInfo: null,
    };
  }

  public componentDidMount = () => {
    if (Platform.OS === 'web') {
      this.loadPaymentInfoInBackground();
    }
  }

  private loadPaymentInfoInBackground = () => {
    this.loadPaymentInfo()
      .catch((e) => { /* handled in loadPaymentInfo */ });
  }

  private loadPaymentInfo = async () => {
    this.setState({ isLoading: true });
    try {
      const [paymentSummary, paymentInfo] = await Promise.all([
        this.userServiceClient.getSubscriptionPaymentSummaryForUser(),
        this.userServiceClient.getPaymentInfo(this.props.user.userProfile.uid),
      ]);
      this.setState({
        paymentInfo,
        paymentSummary,
      });
    } catch (e) {
      Logger.notify(e);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private activatePendingPlan = async () => {
    try {
      this.setState({ activatePendingLoading: true });
      const { paymentInfo: { currentPlan: { subscription: { id: subscriptionId } } } } = this.state;
      const { user: { userProfile } } = this.props;

      await this.userServiceClient.activatePendingPlan(userProfile.uid, subscriptionId);

      await this.loadPaymentInfo();
    } catch (e) {
      Logger.notify(e);
    } finally {
      this.setState({ activatePendingLoading: false });
    }
  }

  private openCustomerPortal = async () => {
    const { t } = this.props;
    this.setState({ customerPortalLoading: true });
    try {
      const portalSession = await this.userServiceClient.getCustomerPortalSession();
      await openLink(portalSession.url);
    } catch (e) {
      Logger.notify(e);
      showMessage(t('openCustomerPortalError'));
    } finally {
      this.setState({ customerPortalLoading: false });
    }
  }

  private getDetailsComponent = () => {
    const { t, user: { userProfile }} = this.props;
    const { paymentSummary } = this.state;
    const hasPlan = paymentSummary && paymentSummary.length > 0;

    if (!hasPlan) {
      const userIsOnFamilyPlan = !!userProfile.planOwnerUserProfileId;
      return (
        <>
          <Text style={[styles.heading, { paddingTop: defaultScale(15) }]}>{t('currentSubscription')}</Text>
          <Text style={[styles.textMuted, styles.noPlanText]}>{t('currentlyOnNoPlan')}</Text>
          {!userIsOnFamilyPlan &&
            <Button
              onPress={this.subscribeButtonPress}
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              title={t('subscribe')}
            />
          }
        </>
      );
    }

    const currentSubscription = paymentSummary[0];
    const isInAppPurchase = currentSubscription.accountStatus === 'InAppPurchase';

    if (isInAppPurchase) {
      return (
        <>
          <Text style={[styles.heading, { paddingTop: defaultScale(15) }]}>{t('currentSubscription')}</Text>
          <Text style={[styles.textMuted, { paddingVertical: defaultScale(15) }]}>
            {t('iapSubscriptionActive')}</Text>
          <Text style={styles.textMuted}>{t('manageIAPInstructions')}</Text>
        </>
      );
    }

    const total = parseFloat(currentSubscription.totalIncludingOnceOffPrices!.toFixed(2));
    if (currentSubscription.isTrialPeriodActive) {
      return (
        <>
          <Text style={styles.boldText}>{t('currentSubscription')}</Text>
          <Text style={styles.heading}>{t('orderSummary')}</Text>
          <TrialSubscriptionSummary
            t={this.props.t}
            total={total}
            paymentSubscriptionSummary={currentSubscription}
            showingExistingSubscription
          />
        </>
      );
    }

    return (
      <>
        <Text style={styles.heading}>{t('currentSubscription')}</Text>
        <PaymentSummaryDisplay
          t={this.props.t}
          paymentSubscriptionSummary={currentSubscription}
          showOrderSummaryNote={false}
        />
      </>
    );
  }

  private subscribeButtonPress = () => {
    this.props.openPaywallModal(this.loadPaymentInfo);
  }

  private navigationDidFocus = () => {
    this.loadPaymentInfoInBackground();
  }

  // tslint:disable-next-line:member-ordering
  public render() {
    const { t, navigation } = this.props;
    const { paymentInfo, activatePendingLoading, paymentSummary, customerPortalLoading } = this.state;
    const showActivateElitePlan = !!paymentInfo?.currentPlan?.subscription.pendingPlanId;

    const currentSubscription = paymentSummary[0];

    return (
      <View style={styles.container} className='full-height'>
        <AppStateFocusedEvents
          onScreenFocus={this.navigationDidFocus}
          navigation={navigation}
        />
        {this.state.isLoading &&
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={this.state.isLoading}
            style={styles.loader}
          />
        }
        {this.state.paymentInfo && !this.state.isLoading &&
          <>
            <ScrollView keyboardShouldPersistTaps='handled'>
              <View>
                <View style={[styles.innerContainer]}>
                  {this.getDetailsComponent()}
                </View>
              </View>
            </ScrollView>
            {showActivateElitePlan &&
              <>
                <Text style={styles.activateEliteDescription}>{t('activateElitePlanDescription')}</Text>
                <Button onPress={this.activatePendingPlan} isLoading={activatePendingLoading}
                        fontColor={Theme.WHITE} title={t('activateElitePlan')} />
              </>
            }
            {currentSubscription?.allowSelfManagementOfSubscription &&
              <Button onPress={this.openCustomerPortal} isLoading={customerPortalLoading}
                      buttonType={ButtonTypes.OUTLINE}
                      title={t('manageSubscriptionAndPayments')} />
            }
          </>
        }
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: defaultScale(15),
    ...Platform.select({
      web: {
        paddingVertical: 15,
        paddingHorizontal: 30,
      },
    }),
    backgroundColor: Theme.WHITE,
  },
  loader: {
    height: '100%',
    justifyContent: 'center',
  },
  heading: {
    ...Theme.textStyle.heading,
    color: Theme.CHARCOAL,
  },
  boldText: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.CHARCOAL,
    marginBottom: defaultScale(5),
  },
  innerContainer: {
    paddingVertical: defaultScale(10),
  },
  textMuted: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
  },
  noPlanText: {
    paddingTop: defaultScale(20),
    paddingBottom: defaultScale(50),
  },
  activateEliteDescription: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK_MEDIUM,
    paddingVertical: defaultScale(20),
    textAlign: 'center',
  },
});

const mapDispatchToProps = (dispatch) => ({
  openPaywallModal: (onJoinSuccess) => dispatch(openPaywallModalAction(PaywallPage.Workout, onJoinSuccess)),
});

export default connect(null, mapDispatchToProps)(
  translate(['payments'], { wait: true })(UserDataConsumer(BillingTab)),
);
